import React, { useCallback, useState } from 'react';
import { useSelected, useFocused } from 'slate-react';
import { Settings, X } from 'lucide-react';

// Divider style options
const DIVIDER_STYLES = {
  solid: 'Solid',
  dashed: 'Dashed',
  dotted: 'Dotted',
  double: 'Double',
};

function DividerElement({ attributes, children, element, onUpdate, onDelete }) {
  const selected = useSelected();
  const focused = useFocused();
  const [showSettings, setShowSettings] = useState(false);

  const {
    style = 'solid',
    thickness = 1,
    color = '#e5e7eb',
    spacing = 16,
  } = element;

  const handleStyleChange = useCallback((newStyle) => {
    if (onUpdate) {
      onUpdate(element.id, { style: newStyle });
    }
  }, [element.id, onUpdate]);

  const handleThicknessChange = useCallback((newThickness) => {
    if (onUpdate) {
      onUpdate(element.id, { thickness: parseInt(newThickness) });
    }
  }, [element.id, onUpdate]);

  const handleColorChange = useCallback((newColor) => {
    if (onUpdate) {
      onUpdate(element.id, { color: newColor });
    }
  }, [element.id, onUpdate]);

  const handleSpacingChange = useCallback((newSpacing) => {
    if (onUpdate) {
      onUpdate(element.id, { spacing: parseInt(newSpacing) });
    }
  }, [element.id, onUpdate]);

  // Proper event handlers for Slate
  const handleSettingsClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(!showSettings);
  }, [showSettings]);

  const handleDeleteClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(element.id);
    }
  }, [element.id, onDelete]);

  const handleCloseSettings = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(false);
  }, []);

  const dividerStyle = {
    borderTopStyle: style,
    borderTopWidth: `${thickness}px`,
    borderTopColor: color,
    marginTop: `${spacing}px`,
    marginBottom: `${spacing}px`,
  };

  return (
    <div 
      {...attributes}
      contentEditable={false}
      className={`divider-element relative group ${
        selected && focused ? 'bg-blue-50 rounded-lg' : ''
      }`}
    >
      {children}
      
      {/* Divider Line */}
      <div className="relative">
        <hr style={dividerStyle} />
        
        {/* Controls - visible when selected or on hover */}
        <div 
          className={`absolute -top-3 right-0 flex items-center gap-1 transition-opacity ${
            selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          contentEditable={false}
        >
          <button
            type="button"
            onMouseDown={handleSettingsClick}
            className="p-1 bg-white rounded shadow-md hover:bg-gray-100 cursor-pointer"
            title="Settings"
          >
            <Settings size={14} />
          </button>
          {onDelete && (
            <button
              type="button"
              onMouseDown={handleDeleteClick}
              className="p-1 bg-white rounded shadow-md hover:bg-red-100 cursor-pointer"
              title="Delete"
            >
              <X size={14} className="text-red-500" />
            </button>
          )}
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div 
            className="absolute top-4 right-0 bg-white rounded-lg shadow-xl border p-4 z-20 min-w-[240px]"
            contentEditable={false}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h4 className="font-semibold mb-3 text-sm">Divider Settings</h4>
            
            {/* Style */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Style</label>
              <select
                value={style}
                onChange={(e) => handleStyleChange(e.target.value)}
                className="w-full border rounded-md px-2 py-1.5 text-sm"
              >
                {Object.entries(DIVIDER_STYLES).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Thickness */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Thickness: {thickness}px
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={thickness}
                onChange={(e) => handleThicknessChange(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Color */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="w-8 h-8 border rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="flex-1 border rounded-md px-2 py-1.5 text-sm"
                />
              </div>
            </div>

            {/* Spacing */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Spacing: {spacing}px
              </label>
              <input
                type="range"
                min="0"
                max="64"
                value={spacing}
                onChange={(e) => handleSpacingChange(e.target.value)}
                className="w-full"
              />
            </div>

            <button
              type="button"
              onMouseDown={handleCloseSettings}
              className="w-full mt-2 px-3 py-1.5 bg-gray-100 rounded-md text-sm hover:bg-gray-200 cursor-pointer"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DividerElement;
